import { useEffect, useRef, useState } from "react";
import { css, styled } from "styled-components";

import { CrossIcon, FullscreenIcon, MediaPlayer, PictureInPictureIcon, UIButton, WebFrame } from "common/components";
import { ToggleableOverlay } from "common/components/Overlay";
import { zIndex } from "common/constants/zIndex";
import { breakpoints, breakpointStyles, breakWordStyle, easing } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useMediaQuery } from "common/utilities/useMediaQuery";
import { useTranslate } from "common/utilities/useTranslate";
import { SplashStyles } from "domains/resources/SplashStyles";
import { VideoCard } from "domains/resources/VideoCard";
import { setVisualMediaPlayer } from "domains/visualMediaPlayer/actions";
import { getVisualMediaPlayerFile } from "domains/visualMediaPlayer/selectors";
import { MediaType } from "domains/visualMediaPlayer/types";

const { StyledSplash, StyledCaption, StyledColumn } = SplashStyles;

const Container = styled.div<{ isMinimized?: boolean; isOpen: boolean }>`
  max-width: 100%;
  min-width: 100%;
  max-height: 90%;
  transform: translate3d(${({ isOpen }) => (isOpen ? 0 : -120)}%, 0, 0);
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  position: fixed;
  border-radius: 5px;
  z-index: ${zIndex.mediaPlayer};
  bottom: ${breakpointStyles.medium.spacing}rem;
  left: ${breakpointStyles.medium.spacing}rem;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ isMinimized }) =>
    isMinimized
      ? css`
          max-width: 450px;
          min-width: 450px;
          transition:
            transform 0.3s ${easing.slightBounce},
            max-width 0.3s,
            min-width 0.3s,
            top 0.3s;
        `
      : css`
          max-width: calc(100% - (${breakpointStyles.medium.spacing * 2}rem));
          min-width: calc(100% - (${breakpointStyles.medium.spacing * 2}rem));
          top: ${breakpointStyles.medium.spacing}rem;
          transition:
            opacity 0.3s,
            max-width 0.3s,
            min-width 0.3s,
            top 0.3s;
        `}
`;

const Content = styled.div`
  position: relative;
  max-height: calc(100% - 6rem);
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  background-color: rgba(0, 0, 0, 0.8);
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  width: 100%;
`;

const iconStyle = css`
  padding: 10px;
  width: 14px;
  height: 14px;
  color: white;
  cursor: pointer;
`;

const Minimise = styled(PictureInPictureIcon)`
  ${iconStyle};
`;

const Fullscreen = styled(FullscreenIcon)`
  ${iconStyle};
`;

const CloseIcon = styled(CrossIcon)`
  ${iconStyle};
`;

const Video = styled(MediaPlayer)`
  border: none;
  overflow: hidden;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
`;

const Frame = styled(WebFrame)`
  border: none;
  overflow: hidden;
  height: 100%;
  padding-top: 0;
  margin: 0;
`;

const Popover = styled.div`
  box-sizing: border-box;
  transition:
    opacity 0.3s,
    bottom 0.3s;
  opacity: 0;
  position: absolute;
  padding: 10px;
  text-align: center;
  left: -30px;
  width: 100px;
  bottom: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 6px;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid ${({ theme }) => theme.borders.normal};
  z-index: ${zIndex.mediaPlayerButtonPopover};
  color: white;

  ${breakWordStyle}
`;

const IconContainer = styled.div`
  position: relative;

  &:hover {
    ${Popover} {
      opacity: 1;
      bottom: calc(100% + 2px);
    }
  }
`;

const Splash = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  box-sizing: border-box;
  display: flex;
  height: calc(100% - 56px - 8px - 32px);
  inset: 38px 0 0 0;
  justify-content: center;
  padding: 4rem;
  position: absolute;
  width: 100%;

  @media ${breakpoints.medium} {
    height: calc(100% - 56px - 8px);
  }
`;

export const CloseButton = styled(UIButton)`
  aspect-ratio: 1 / 1;
  padding: 0;
  background-color: transparent;
`;

export const VisualMediaPlayerProvider = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const isResizable = useMediaQuery("(min-width: 768px) and (min-height: 768px)");
  const mediaFile = useSelector(getVisualMediaPlayerFile);
  const [isOpen, setIsOpen] = useState<boolean>(!!mediaFile);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [showSplash, setShowSplash] = useState<"full" | "empty" | "none">("none");
  const translate = useTranslate();
  const containerRef = useRef<HTMLDivElement>(null);

  const isVideoViaDirectFileLink = mediaFile?.mediaType !== MediaType.knowledgeData;

  useEffect(() => {
    if (mediaFile?.id) {
      setShowSplash("empty");
      setTimeout(() => setShowSplash("none"), 200);
    }
  }, [mediaFile?.id]);

  useEffect(() => {
    if (isOpen !== !!mediaFile) {
      window.addEventListener("keyup", handleKeyPress);
      setIsOpen(!!mediaFile);
      setIsMinimized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaFile]);

  useEffect(() => {
    if (isOpen) containerRef.current?.focus();
  }, [isOpen]);

  const handleKeyPress = (event: KeyboardEvent) => (event.code === "Escape" ? closePlayer() : "");

  const closePlayer = () => {
    setShowSplash("none");
    dispatch(setVisualMediaPlayer(undefined));
    if (mediaFile?.onCloseRoute !== undefined) go(mediaFile.onCloseRoute);
    window.removeEventListener("keyup", handleKeyPress);
    setIsOpen(false);
  };

  const toggleSize = () => setIsMinimized(!isMinimized);

  return (
    <>
      <ToggleableOverlay isActive={isOpen && !isMinimized} />
      <Container
        ref={containerRef}
        isMinimized={isMinimized}
        isOpen={isOpen}
        // tabindex=-1 is needed for accessibility, so the modal can be programatically focused for screen readers.
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
        aria-label={`Visual Media Player Opened for Media File ${mediaFile?.name}`}
      >
        {mediaFile?.url && (
          <Content data-test-id="visual-media-player">
            <ButtonContainer>
              {isVideoViaDirectFileLink && isResizable && (
                <IconContainer>
                  {isMinimized ? <Fullscreen onClick={toggleSize} /> : <Minimise onClick={toggleSize} />}
                  <Popover>{translate(isMinimized ? "app.fullscreen.title" : "app.fullscreen_exit.title")}</Popover>
                </IconContainer>
              )}
              <IconContainer>
                <CloseButton onClick={closePlayer} variant="neutral" aria-label="Close Visual Media Player">
                  <CloseIcon role="button" />
                </CloseButton>
                <Popover inert={true}>{translate("app.dialog.close")}</Popover>
              </IconContainer>
            </ButtonContainer>
            {isVideoViaDirectFileLink && (
              <Video
                url={mediaFile.url}
                title={mediaFile.name}
                isVideo={mediaFile?.mediaType === MediaType.videoViaDirectFileLink}
                onEnded={() => setShowSplash("full")}
                onPauseOrPlay={() => setShowSplash("none")}
                isYoutubeVideo={mediaFile.isYoutubeVideo}
              />
            )}
            {!isVideoViaDirectFileLink && <Frame url={mediaFile.url} title={mediaFile.name} forceIframe />}
            {showSplash !== "none" && mediaFile.splash !== undefined && (
              <Splash>
                {showSplash === "full" && (
                  <StyledSplash>
                    {mediaFile.splash.previous !== undefined && (
                      <StyledColumn>
                        <StyledCaption>{translate("app.resources.video.previous")}</StyledCaption>
                        <VideoCard {...mediaFile.splash.previous} />
                      </StyledColumn>
                    )}
                    {mediaFile.splash.next !== undefined && (
                      <StyledColumn>
                        <StyledCaption>{translate("app.resources.video.next")}</StyledCaption>
                        <VideoCard {...mediaFile.splash.next} />
                      </StyledColumn>
                    )}
                  </StyledSplash>
                )}
              </Splash>
            )}
          </Content>
        )}
      </Container>
    </>
  );
};
